"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState, useEffect } from "react";
import { Leaf, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Testimonials() {
  const testimonials = [{
    quote: "Lush Fashion has completely transformed my wardrobe. The quality of the fabrics and the attention to detail in every piece is simply unmatched.",
    author: "Sarah M.",
    role: "Fashion Stylist",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_sarah_m.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_sarah_m.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_sarah_m.webp-wvc-sizes"
  }, {
    quote: "I love how Lush Fashion balances modern trends with timeless elegance. Every item I've purchased feels like a premium investment.",
    author: "David K.",
    role: "Creative Director",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_david_k.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_david_k.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_david_k.png-wvc-sizes"
  }, {
    quote: "The shopping experience at Lush Fashion is seamless, and the collections are always ahead of the curve. Truly a premium destination.",
    author: "Elena R.",
    role: "Lifestyle Influencer",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_elena_r.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_elena_r.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/customer_elena_r.webp-wvc-sizes"
  }];
  const [active, setActive] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useEffect(() => {
    const timer = setInterval(() => {
      setActive(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);
  const current = testimonials[active];
  return <section data-section-id="206" id="customerreviews" className="bg-secondary text-foreground py-24 md:py-32 px-6 overflow-hidden">
      <div ref={ref} className={`max-w-4xl mx-auto text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="flex items-center justify-center gap-4 mb-12 md:mb-16">
          <Leaf className="size-3.5 text-accent/60 -rotate-45" strokeWidth={1.5} />
          <span className="font-default text-[0.7rem] md:text-xs tracking-[0.35em] uppercase text-foreground/70">
            Client Testimonials
          </span>
          <Leaf className="size-3.5 text-accent/60 rotate-45" strokeWidth={1.5} />
        </div>

        {}
        <div className="relative min-h-[300px] flex items-center justify-center">
          {}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
            <span className="text-[20rem] font-serif text-accent/10 leading-none transform -translate-x-1/2 -translate-y-1/4">
              &ldquo;
            </span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="relative z-10 w-full">
              <blockquote className="font-default italic text-foreground leading-relaxed max-w-2xl mx-auto text-xl md:text-2xl">
                &ldquo;{current.quote}&rdquo;
              </blockquote>

              <div className="flex justify-center my-10 md:my-12">
                <span className="block h-px w-16 bg-accent/30" />
              </div>

              <div className="flex items-center justify-center gap-4">
                <SafeHydrate><Avatar className="size-12 rounded-lg ring-1 ring-border">
                  <AvatarImage src={current.avatar} data-wvc-srcset={current.srcset} data-wvc-sizes={current.sizes} alt={current.author} />
                  <AvatarFallback className="font-default text-xs bg-muted text-muted-foreground rounded-lg">
                    {current.author.split(" ").map(n => n[0]).join("")}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span className="heading text-base tracking-wide text-foreground">
                      {current.author}
                    </span>
                    <div className="flex items-center justify-center size-4 bg-accent rounded-lg">
                      <Check className="size-2.5 text-white" strokeWidth={3} />
                    </div>
                  </div>
                  <div className="font-default italic text-sm text-muted-foreground mt-0.5">
                    {current.role}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {}
        <div className="flex items-center justify-center gap-4 mt-14 md:mt-16">
          {testimonials.map((t, i) => <Button key={i} data-index={i} variant="ghost" size="sm" onClick={() => setActive(i)} aria-label={`Show testimonial from ${t.author}`} className="h-auto p-0 hover:bg-transparent group">
              <span className={`block size-3 transition-all duration-300 ${i === active ? "bg-accent scale-110" : "bg-primary/20 group-hover:bg-primary/40"}`} />
            </Button>)}
        </div>
      </div>
    </section>;
}